import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import useCountdown from '../../hooks/useCountdown';

interface CountdownTimerProps {
  expiresAt: string;
  className?: string;
}

export default function CountdownTimer({ expiresAt, className = '' }: CountdownTimerProps) {
  const { t } = useTranslation('common');
  const timeLeft = useCountdown(expiresAt);

  const getTimeString = () => {
    if (timeLeft.days > 0) {
      return t('time.countdown.days', { count: timeLeft.days });
    }
    if (timeLeft.hours > 0) {
      return t('time.countdown.hours', { count: timeLeft.hours });
    }
    if (timeLeft.minutes > 0) {
      return t('time.countdown.minutes', { count: timeLeft.minutes });
    }
    return t('time.countdown.seconds', { count: timeLeft.seconds });
  };

  const getColorClass = () => {
    if (timeLeft.days > 0) return 'text-green-600 bg-green-50';
    if (timeLeft.hours > 12) return 'text-blue-600 bg-blue-50';
    if (timeLeft.hours > 0) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const formatExpirationDate = (date: string) => {
    const expiresDate = new Date(date);
    return new Intl.DateTimeFormat('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(expiresDate);
  };

  return (
    <div className="space-y-1">
      <div 
        className={`
          inline-flex items-center px-3 py-1 
          rounded-full text-sm font-medium 
          ${getColorClass()} ${className}
        `}
        role="timer"
        aria-label={t('time.expiration.expires_in', { time: getTimeString() })}
      >
        <Clock className="w-4 h-4 mr-2" />
        <span>{formatExpirationDate(expiresAt)} ({getTimeString()})</span>
      </div>
    </div>
  );
}